/**
 * User Permissions Hook
 * Handles role-based permissions for the application
 */

import { useQuery } from "@tanstack/react-query";
import { useCallback } from "react";
import { useUser } from "../contextapi/UserContext";
import { offlineSupabase } from "../utils/offlineSupabaseProxy";
import { queryKeys } from "../utils/queryKeys";

export interface UserPermissions {
  // Global permissions
  isGuest: boolean;
  isOrgAdmin: boolean;
  isOrgMember: boolean;
  isPlatformAdmin: boolean;
  platformAdminLevel?: string;

  // Team permissions
  teamPermissions: {
    [teamId: string]: {
      isGroupAdmin: boolean;
      isGroupMember: boolean;
      canManageTeam: boolean;
      canViewTeam: boolean;
    };
  };

  // Feature permissions
  canEditProfile: boolean;
  canViewOrganizations: boolean;
  canEditOrganizations: boolean;
  canManageTeams: boolean;
  canCreateTeams: boolean;
  canEditBranding: boolean;
  canViewSecurity: boolean;
  canEditSecurity: boolean;
  canViewSubscription: boolean;
  canEditSubscription: boolean;
  canEditPreferences: boolean;
}

export interface UserRoleData {
  profileRole: string | null;
  organizationRole: string | null;
  organizationId: string | null;
  platformAdminLevel: string | null;
  teamRoles: Array<{
    teamId: string;
    teamName: string;
    role: string;
  }>;
}

const calculatePermissions = (roleData: UserRoleData): UserPermissions => {
  const isGuest = roleData.profileRole === "guest" || !roleData.organizationRole;
  const isOrgAdmin = roleData.organizationRole === "org_admin";
  const isOrgMember = roleData.organizationRole === "org_member";
  const isPlatformAdmin = !!roleData.platformAdminLevel;

  const teamPermissions: UserPermissions["teamPermissions"] = {};
  roleData.teamRoles.forEach(teamRole => {
    const isGroupAdmin = teamRole.role === "group_admin";
    const isGroupMember = teamRole.role === "group_member" || isGroupAdmin;

    teamPermissions[teamRole.teamId] = {
      isGroupAdmin,
      isGroupMember,
      canManageTeam: isGroupAdmin || isOrgAdmin,
      canViewTeam: isGroupMember || isOrgAdmin,
    };
  });

  return {
    isGuest,
    isOrgAdmin,
    isOrgMember,
    isPlatformAdmin,
    platformAdminLevel: roleData.platformAdminLevel ?? undefined,
    teamPermissions,
    canEditProfile: true,
    canViewOrganizations: !isGuest || isPlatformAdmin,
    canEditOrganizations: isOrgAdmin || isPlatformAdmin,
    canManageTeams: isOrgAdmin || isOrgMember || isPlatformAdmin,
    canCreateTeams: isOrgAdmin || isOrgMember || isPlatformAdmin,
    canEditBranding: isOrgAdmin || isPlatformAdmin,
    canViewSecurity: isOrgAdmin || isPlatformAdmin,
    canEditSecurity: isOrgAdmin || isPlatformAdmin,
    canViewSubscription: isOrgAdmin || isOrgMember || isPlatformAdmin,
    canEditSubscription: isOrgAdmin || isPlatformAdmin,
    canEditPreferences: true,
  };
};

export function useUserPermissions() {
  const { user } = useUser();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: queryKeys.user.permissions(user?.id ?? ''),
    queryFn: async () => {
      const { data: permissionsData, error: permissionsError } = await offlineSupabase
        .rpc("get_user_permissions", { user_id_param: user!.id });

      if (permissionsError) throw permissionsError;

      const profile = permissionsData.user_context?.profile;
      const orgMemberships = permissionsData.user_context?.organizations || [];
      const teamMemberships = permissionsData.user_context?.teams || [];
      const platformAdmin = permissionsData.user_context?.platform_admin;

      const orgMembership = orgMemberships.length > 0 ? orgMemberships[0] : null;

      const roleData: UserRoleData = {
        profileRole: profile?.role || null,
        organizationRole: orgMembership?.role || null,
        organizationId: orgMembership?.organisation_id || null,
        platformAdminLevel: platformAdmin?.admin_level || null,
        teamRoles: (teamMemberships || []).map((tm: { group_id: string; group_name: string; role: string }) => ({
          teamId: tm.group_id,
          teamName: tm.group_name,
          role: tm.role,
        })),
      };

      return {
        roleData,
        permissions: calculatePermissions(roleData),
      };
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
  });

  const hasTeamPermission = useCallback((teamId: string, permission: keyof UserPermissions["teamPermissions"][string]) => {
    if (!data?.permissions?.teamPermissions[teamId]) return false;
    return data.permissions.teamPermissions[teamId][permission];
  }, [data?.permissions]);

  const canManageSpecificTeam = useCallback((teamId: string) => {
    return hasTeamPermission(teamId, "canManageTeam");
  }, [hasTeamPermission]);

  return {
    permissions: data?.permissions ?? null,
    roleData: data?.roleData ?? null,
    loading: isLoading,
    error: error instanceof Error ? error.message : null,
    refreshPermissions: refetch,
    hasTeamPermission,
    canManageSpecificTeam,
  };
}
